/**
 * What chord symbols and figured bass have in common: they stand at a tick of a bar.
 *
 * Everything else in a part hangs on a note -- an articulation, a slur, a word of a lyric --
 * and moves or dies with it. These two do not. A chord stands over a beat and a figure
 * stands under one, whether or not anything is written there, so both are positioned by
 * ticks from the start of their bar and both are edited through the bar rather than through
 * an event.
 *
 * That makes the same four questions come up twice: which bar am I in, what tick is this
 * note at, how do I write the bar back, and which ticks does the selection cover. They are
 * answered once here so that the two layers cannot drift apart in how they answer them.
 */

import type { Command } from "../commands/types";
import { capacityOf, measureOnset, occupiedTicks } from "../document/query";
import type { EventLocation } from "../document/query";
import type { PartMeasure, Score } from "../document/types";
import { selectedLocations } from "./selection";
import type { Selection } from "./selection";

/** The bar a location is in, or `null` when the part is silent there. */
export function barOf(score: Score, at: EventLocation): PartMeasure | null {
  return score.parts.find((part) => part.id === at.partId)?.measures[at.measureId] ?? null;
}

/** Ticks from the start of the bar to an event, which is where its mark stands. */
export function tickInBar(score: Score, at: EventLocation): number {
  return at.onset - measureOnset(score, at.measureIndex);
}

/** One command replacing part of a bar, which is how every mark on a tick is written. */
export function rewriteBar(
  at: EventLocation,
  bar: PartMeasure,
  patch: Partial<PartMeasure>,
): Command {
  return {
    kind: "setPartMeasure",
    partId: at.partId,
    measureId: at.measureId,
    measure: { ...bar, ...patch },
  };
}

/** One bar's worth of what the selection covers: the stretches of ticks it occupies. */
export interface CoveredBar {
  readonly at: EventLocation;
  readonly ranges: readonly (readonly [number, number])[];
}

/**
 * The ticks the selection covers, bar by bar.
 *
 * A selected note covers the ticks it lasts, not only the one it starts on, so a passage
 * selected across a bar takes in every mark standing under it. That is what a user who
 * selects a phrase and clears it means, and leaving one behind in the middle of a note
 * nobody can see is worse than taking one too many.
 */
export function coveredBars(score: Score, selection: Selection): readonly CoveredBar[] {
  const spans = new Map<string, { at: EventLocation; ranges: [number, number][] }>();
  for (const at of selectedLocations(score, selection)) {
    const capacity = capacityOf(score, at.measureIndex);
    const start = tickInBar(score, at);
    const key = `${at.partId}:${at.measureId}`;
    const entry = spans.get(key) ?? { at, ranges: [] };
    entry.ranges.push([start, start + occupiedTicks(at.event, capacity)]);
    spans.set(key, entry);
  }
  return [...spans.values()];
}

/** Whether a tick falls inside any of the stretches the selection covers. */
export function covers(ranges: CoveredBar["ranges"], onset: number): boolean {
  return ranges.some(([from, to]) => onset >= from && onset < to);
}
